import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { QuestionnaireService } from '../../../js/api';
import type { QuestionnaireSummaryItem } from '../../../js/api';
import { providerQueryKeys } from '../../../js/app/query-keys-factories/providers';
import { downloadQuestionnairePdf } from '../utils/downloadQuestionnairePdf';
import { Dialog, DialogBody, DialogCloseButton, DialogTitle } from './dialog';
import { DrawerField, DrawerSectionHeader } from './drawer-components';
import { ProviderQuestionnaireOrgView } from './provider-questionnaire-org-view';
import { Spinner } from './spinner';
import { TooltippedText } from './tooltipped-text';

/**
 * What the summary has to say — built by the backend
 * (`ProviderQuestionnaireService.summarize`) so every surface that shows it, and the
 * printable presentation, say the same thing. `locked` is the organization that has
 * not cleared the clinician yet (the questionnaire endpoint answers 403).
 */
export type MalpracticeSummary = {
  state: 'locked' | 'no_response' | 'nothing_reported' | 'reported';
  items: Array<QuestionnaireSummaryItem>;
};

const NO_RESPONSE = (
  <div className="tw:text-sm tw:text-zinc-700 tw:ml-5.75">
    <i className="fas fa-triangle-exclamation tw:text-stoplight-warning tw:mr-1" />
    No response yet from clinician
  </div>
);

// An organization is refused the questionnaire (403) until it has cleared the clinician.
const NOT_CLEARED_YET = (
  <div className="tw:text-sm tw:text-zinc-700 tw:ml-5.75" data-testid="malpractice-locked">
    <i className="fas fa-lock tw:text-zinc-400 tw:mr-1" />
    Available once this clinician is cleared for your organization
  </div>
);

/**
 * "Malpractice & Other History": the inline summary of a clinician's questionnaire
 * (licensure, professional liability & claims, background, job performance) plus a
 * View link that opens the full questionnaire in a dialog. The dialog offers
 * "Download PDF": a paper copy generated from the current answers on demand (nothing
 * is stored), stamped by the server with the clinician, the print date and who printed.
 *
 * One implementation for every drawer that shows it (connection Presentation Info,
 * staff clinician drawer Insurance tab), so what appears when is the same everywhere:
 * - loading → spinner
 * - refused (403: an organization that has not cleared the clinician yet) → "Available
 *   once this clinician is cleared", and no View control
 * - no questionnaire on file (the endpoint 404s) or an empty one → "No response yet",
 *   and no View control (there is nothing to open)
 * - every question answered NO → the green "nothing reported" line
 * - otherwise → each YES question with its comment
 *
 * `viewControl` only moves the header controls, never what is shown:
 * - `link` (default): info icon + "View" link at the right edge of the header — fits a
 *   narrow tab (connection drawer)
 * - `icon`: an eye button then the info icon right after the title — for a full-width
 *   section, where a link at the far edge is a long way from the heading
 */
export function MalpracticeHistorySection({
  providerId,
  viewControl = 'link',
  summary: providedSummary,
}: {
  providerId: string;
  viewControl?: 'link' | 'icon';
  /** The summary when the host already holds it (the connection presentation payload);
   *  otherwise the section reads the clinician's questionnaire itself. */
  summary?: MalpracticeSummary;
}) {
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
  const openQuestionnaire = () => setIsQuestionnaireOpen(true);

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadQuestionnaire = async () => {
    setIsDownloading(true);
    try {
      const filename = await downloadQuestionnairePdf(providerId);
      toast.success(`Saved ${filename}`);
    } catch {
      toast.error("Couldn't prepare the questionnaire PDF — please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const {
    data: questionnaireData,
    // react-query v4: a DISABLED query still reports `isLoading`; only a query that is
    // actually fetching counts as loading here (none at all when the summary is given).
    isInitialLoading: isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: providerQueryKeys.questionnaire(providerId),
    queryFn: () => QuestionnaireService.questionnaireRetrieve({ providerId }),
    enabled: !!providerId && !providedSummary,
  });

  const summary: MalpracticeSummary | undefined =
    providedSummary ??
    (isError
      ? {
          state: (error as { status?: number } | null)?.status === 403 ? 'locked' : 'no_response',
          items: [],
        }
      : questionnaireData?.summary);
  const isLocked = summary?.state === 'locked';
  const hasData = !!summary && summary.state !== 'no_response' && !isLocked;

  const renderSummary = () => {
    if (!summary) {
      return (
        <div className="tw:flex tw:items-center tw:py-2 tw:text-sm tw:text-zinc-500">
          <Spinner
            size="sm"
            variant="zinc"
            className="tw:mr-2"
            aria-label="Loading questionnaire"
          />
          Loading malpractice history...
        </div>
      );
    }
    if (isLocked) return NOT_CLEARED_YET;
    if (summary.state === 'no_response') return NO_RESPONSE;
    if (summary.state === 'nothing_reported') {
      return (
        <div className="tw:text-sm tw:text-zinc-700 tw:ml-5.75">
          <i className="fas fa-check-circle tw:text-stoplight-success" /> No Malpractice History or
          other Issues Reported
        </div>
      );
    }
    return (
      <div className="tw:space-y-6">
        {summary.items.map(({ key, question, comment }) => (
          <div key={key} className="tw:space-y-1">
            <div className="tw:ml-5.75 tw:text-zinc-700 tw:text-sm">
              <i className="fas fa-x tw:text-stoplight-danger tw:mr-1" />
              {question}
            </div>
            <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-1 tw:text-sm">
              <DrawerField label="Answer" value="YES" />
            </div>
            {comment ? (
              <div className="tw:mt-1 tw:ml-5.75">
                <div className="tw:text-zinc-400 tw:text-sm">Comment:</div>
                <div className="tw:text-zinc-700 tw:text-sm tw:whitespace-pre-line tw:rounded tw:bg-zinc-50 tw:px-2 tw:py-0.5">
                  {comment}
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  // There is a questionnaire to open only once the clinician has answered: with no
  // response the dialog could show nothing but "unable to load".
  const canView = !isLoading && hasData;

  const title = 'Malpractice & Other History';
  const titleIcon = <i className="fas fa-clipboard-question tw:text-brand-green-0 fa-fw" />;
  const info = (placement: 'left' | 'right') => (
    <TooltippedText
      placement={placement}
      tooltipContent={
        <>
          <div>Any questions with a YES response will be displayed below.</div>
          {canView && (
            <div className="tw:max-w-xs">
              {viewControl === 'icon' ? 'Click the eye' : 'Click View'} to see all of the questions
              asked related to Licensure, Professional Liability & Claims History, Background, and
              Job Performance.
            </div>
          )}
        </>
      }
    >
      <i className="far fa-circle-info tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:text-sm" />
    </TooltippedText>
  );

  return (
    <>
      <section data-testid="malpractice-history">
        {viewControl === 'icon' ? (
          // same type treatment as DrawerSectionHeader, with the controls beside the title
          <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
            {titleIcon}
            <span className="tw:text-brand-green-0 tw:italic tw:text-sm">{title}</span>
            {canView && (
              <TooltippedText tooltipContent="View the full questionnaire">
                <button
                  type="button"
                  onClick={openQuestionnaire}
                  className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:text-sm"
                  aria-label="View the full questionnaire"
                  data-testid="malpractice-view-button"
                >
                  <i className="far fa-eye" />
                </button>
              </TooltippedText>
            )}
            {info('right')}
          </div>
        ) : (
          <DrawerSectionHeader
            text={title}
            icon={titleIcon}
            rightElement={
              <div className="tw:flex tw:items-center tw:gap-2">
                {info('left')}
                {canView && (
                  <button
                    type="button"
                    onClick={openQuestionnaire}
                    className="tw:text-xs tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:underline"
                    data-testid="malpractice-view-button"
                  >
                    View
                  </button>
                )}
              </div>
            }
          />
        )}
        <div className="tw:mt-2">{renderSummary()}</div>
      </section>

      {/* Full questionnaire */}
      <Dialog open={isQuestionnaireOpen} onClose={() => setIsQuestionnaireOpen(false)} size="3xl">
        <div
          className="tw:flex tw:items-center tw:justify-between"
          data-testid="provider-questionnaire-dialog"
        >
          <DialogTitle>Clinician Questionnaire</DialogTitle>
          <div className="tw:flex tw:items-center tw:gap-3">
            {/* A paper copy: generated from the current answers, stamped with the print date */}
            <TooltippedText tooltipContent="Download a printable PDF of the current answers">
              <button
                type="button"
                onClick={downloadQuestionnaire}
                disabled={isDownloading}
                className="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:disabled:opacity-60"
                data-testid="questionnaire-download-button"
              >
                {isDownloading ? (
                  <Spinner size="sm" variant="zinc" aria-label="Preparing the PDF" />
                ) : (
                  <i className="far fa-download" />
                )}
                Download PDF
              </button>
            </TooltippedText>
            <DialogCloseButton onClick={() => setIsQuestionnaireOpen(false)} />
          </div>
        </div>
        <DialogBody>
          <div className="tw:space-y-4">
            <p className="tw:text-sm tw:text-zinc-600">
              The sections below include Licensure, Professional Liability & Claims History,
              Background, and Job Performance.
            </p>
            <ProviderQuestionnaireOrgView providerId={providerId} />
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
}
